import { wccModules } from 'enums/wccModules';
import { ImageHelpers } from 'helpers/images';
import { inject, injectable } from 'inversify';
import { SubscribableOrNullableValue } from 'knockout';
import { EffectsContainer } from 'mixins/withEffects';
import WCCFancyboxBaseVM, { WCCFancyboxBaseVMConfig, WCCFancyboxBaseVMConfigRaw } from './base';
import FancyBoxExtender from 'libraries/fancybox';
import './image.html';

const reactor = require('models/reactor');

export interface WCCFancyboxImageVMConfig extends WCCFancyboxBaseVMConfigRaw {
    src: SubscribableOrNullableValue<string>
    caption?: SubscribableOrNullableValue<string>
    crossorigin?: SubscribableOrNullableValue<string>
}

@injectable()
export default class WCCFancyboxImageVM extends WCCFancyboxBaseVM<WCCFancyboxImageVMConfig> {
    constructor(
        @inject(wccModules.componentConfig) config: WCCFancyboxImageVMConfig,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.effects) effects: EffectsContainer
    ) {
        super(config, info, effects);       
    }

    protected override buildConfig(rawConfig: WCCFancyboxImageVMConfig) {
        const oSrc = ko.flattenComputed(rawConfig.src);
        const oCaption = ko.flattenComputed(rawConfig.caption);
        const oOrigin = ko.flattenComputed(rawConfig.crossorigin);
        
        return <WCCFancyboxBaseVMConfig>{
            ...super.buildConfig(rawConfig),

            data: ko.pureComputed(() => ({
                src: oSrc(),
                type: 'image',
                opts: { 
                    caption: oCaption()
                }
            })),

            validate: () => {
                var src = oSrc();

                if (src == null)
                    return false;

                if (src.toLowerCase().indexOf('.heic') !== -1)
                    return false;

                return true;
            },

            events: {
                onInit: function(instance:any) {                    
                    FancyBoxExtender.FixImgCors(instance);  // overwrite the setBigImage function on fancybox to include crossorigin attribute on img element
                },

                beforeLoad: slides => {
                    const origin = oOrigin();
                                        
                    if (origin != undefined) {
                        //create image which will start loading content using provided origin
                        //so image is cached in CORS mode
                        ImageHelpers.getImage(slides.current.src, origin);
                    }
                },

                afterClose: function () {
                    reactor.dispatchEvent('fancybox-closed');
                },

                beforeClose: function () {
                    reactor.dispatchEvent('fancyBoxClosed');
                },

                afterShow: function (instance, slide) {                    
                    reactor.dispatchEvent('fancybox-image-shown', slide.src);
                },

                beforeShow: function () {
                    reactor.dispatchEvent('fancybox-image-willshow');
                }
            }
        }
    }

    protected override buildOptions(rawConfig: WCCFancyboxImageVMConfig) {
        return <FancyBoxOptions>_(super.buildOptions(rawConfig)).omit('src', 'caption', 'crossorigin');
    }
}